/**
 * Helpers for the league team page and the standings charts (demo-ready PR 5).
 */
import { eid } from '../../utils/entityId';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const CHART_COLORS = ['#00ff88', '#38bdf8', '#f59e0b', '#f472b6', '#a78bfa', '#f87171', '#34d399', '#facc15', '#60a5fa', '#fb923c'];

export const leagueKeyOf = (league) => eid(league) || league?.id;

/** The team's id in a league URL: the team for team and pair entries, the entry for singles players. */
export const entryKeyOf = (entry) => (
  entry?.participant_type === 'user'
    ? entry.league_team_id ?? entry.id
    : entry?.team_short_code || entry?.team_uuid || entry?.team_id
);

export const leagueTeamPath = (league, entry) => `/leagues/${leagueKeyOf(league)}/teams/${entryKeyOf(entry)}`;

/**
 * Open an entry's page. The league URL is first set to the tab the user is on,
 * so the browser back button returns to that tab.
 */
export const openLeagueEntry = (navigate, league, entry, fromTab) => {
  navigate(`/leagues/${leagueKeyOf(league)}?tab=${fromTab}`, { replace: true });
  navigate(leagueTeamPath(league, entry));
};

/** "Jan 6" for 2026-01-06. */
export const formatNight = (night) => {
  const [, month, day] = String(night || '').split('-').map(Number);
  return month && day ? `${MONTHS[month - 1]} ${day}` : 'Undated';
};

/** "52-28", or "10-5-2" with draws. */
export const recordLabel = ({ wins = 0, losses = 0, draws = 0 } = {}) =>
  (draws ? `${wins}-${losses}-${draws}` : `${wins}-${losses}`);

/** "1st", "2nd", "3rd", "4th" … */
export const ordinal = (n) => {
  const v = Number(n) || 0;
  const tens = v % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th');
  return `${v}${suffix}`;
};

const lastOf = (team) => team.history[team.history.length - 1] || { wins: 0, losses: 0, points_for: 0, points_against: 0 };

/** Season win race: one row per night, one key per team (t<league_team_id>). */
export const raceData = (history) => ({
  series: history.teams.map((t, i) => ({ key: `t${t.league_team_id}`, name: t.team_name, color: CHART_COLORS[i % CHART_COLORS.length] })),
  data: history.nights.map((night, i) => Object.fromEntries([
    ['night', formatNight(night)],
    ...history.teams.map((t) => [`t${t.league_team_id}`, t.history[i]?.wins ?? 0]),
  ])),
});

/** Wins by team, most first. */
export const winsByTeam = (history) => history.teams
  .map((t) => ({ name: t.team_name, wins: lastOf(t).wins, losses: lastOf(t).losses }))
  .sort((a, b) => b.wins - a.wins || a.name.localeCompare(b.name));

/** Points for and against by team, in standings order. */
export const pointsByTeam = (history) => history.teams
  .map((t) => ({ name: t.team_name, points_for: lastOf(t).points_for, points_against: lastOf(t).points_against }));

/** Head-to-head bars: "vs Team Marie 11-5". */
export const headToHeadData = (rows) => rows.map((r) => ({
  label: `vs ${r.team_name} ${recordLabel(r)}`,
  wins: r.wins,
  losses: r.losses,
  draws: r.draws,
}));
